import { useMutation } from '@vue/apollo-composable';

import type { KkmExecutionResult, KkmServerAdapter } from '@/shared/api';

import {
  classifyCardCancel,
  kkmServerDeviceKey,
  mapCardCancelCommand,
  mapCardReturnCommand,
  rrnUniversalId,
} from '@/entities/fiscalization';
import { paymentQueryNames } from '@/entities/payment';
import { apolloOperationGate, getKkmServerAdapter } from '@/shared/api';
import { getGraphQLErrorCode, requirePayload } from '@/shared/lib';
import {
  CompleteOrderFiscalPaymentReturnDocument,
  type CompleteOrderFiscalPaymentReturnInput,
  type OrderFiscalOperationFieldsFragment,
  OrderFiscalPaymentReturnCommand,
  OrderFiscalPaymentReturnOutcome,
  StartOrderFiscalPaymentReturnDocument,
  type StartOrderFiscalPaymentReturnMutation,
} from '@shared/api/graphql/gql/graphql';

import { executeKkmSafely, isProvenNotRun } from './execute-kkm-safely';
import { toBankEvidenceInput } from './order-fiscal-result';

type PaymentReturnExecution = StartOrderFiscalPaymentReturnMutation['startOrderFiscalPaymentReturn'];

/**
 * Итог возврата по платежу, отменённому без проверки:
 * - `returned` — деньги вернулись (или терминал не ответил внятно: возврат считается выполненным);
 * - `not_charged` — терминал ответил, что списания не было;
 * - `not_run` — команда доказанно не выполнялась, возврат можно начать заново;
 * - `approval_required` — платёж другой смены: нужен одобренный запрос на возврат;
 * - `rrn_required` — нет RRN: без него терминал не найдёт операцию ни для отмены, ни для возврата.
 */
export type CancelledPaymentReturnResult =
  'approval_required' | 'not_charged' | 'not_run' | 'returned' | 'rrn_required';

export interface CancelledPaymentReturnServices {
  complete: (input: CompleteOrderFiscalPaymentReturnInput) => Promise<unknown>;
  start: (operationId: string) => Promise<PaymentReturnExecution>;
}

/** Возврат по отменённому платежу; повторный вызов после перезагрузки дочитывает исход начатой команды. */
export async function returnCancelledPayment(
  adapter: Pick<KkmServerAdapter, 'execute' | 'getResult'>,
  services: CancelledPaymentReturnServices,
  operation: Pick<OrderFiscalOperationFieldsFragment, 'id' | 'return_command_id'>,
  rrn: string | null,
): Promise<CancelledPaymentReturnResult> {
  let execution: PaymentReturnExecution;

  try {
    execution = await services.start(operation.id);
  } catch (error) {
    if (getGraphQLErrorCode(error) === 'order_refund_approval_required') return 'approval_required';
    throw error;
  }

  const deviceKey = kkmServerDeviceKey('terminal', execution.terminal_device_id);

  // UniversalID у отменённого без проверки платежа нет: отмена без RRN ушла бы на терминал вслепую.
  if (operation.return_command_id == null && !rrn) {
    // Команда не уходила: отметку снимаем, иначе следующая попытка читала бы исход несуществующей команды.
    await services.complete({ operationId: operation.id, outcome: OrderFiscalPaymentReturnOutcome.NotRun });
    return 'rrn_required';
  }

  const result =
    operation.return_command_id != null
      ? await executeKkmSafely(() => adapter.getResult(execution.id_command, { deviceKey, timeoutClass: 'acquiring' }))
      : await executeKkmSafely(() =>
          adapter.execute(returnCommand(execution, rrn), { deviceKey, timeoutClass: 'acquiring' }),
        );

  return finish(services, operation.id, execution, result);
}

async function finish(
  services: CancelledPaymentReturnServices,
  operationId: string,
  execution: PaymentReturnExecution,
  result: KkmExecutionResult,
): Promise<CancelledPaymentReturnResult> {
  if (result.kind === 'ok') {
    await services.complete({
      bank: result.acquiring === null ? null : toBankEvidenceInput(result.acquiring),
      operationId,
      outcome: OrderFiscalPaymentReturnOutcome.Returned,
    });
    return 'returned';
  }

  if (execution.command === OrderFiscalPaymentReturnCommand.Cancel && classifyCardCancel(result) === 'not_charged') {
    await services.complete({ operationId, outcome: OrderFiscalPaymentReturnOutcome.NotCharged });
    return 'not_charged';
  }

  if (isProvenNotRun(result)) {
    await services.complete({ operationId, outcome: OrderFiscalPaymentReturnOutcome.NotRun });
    return 'not_run';
  }

  // Неясный возврат считается выполненным: двойной возврат никто не заметит, недошедший клиент заметит.
  console.warn('[useCancelledPaymentReturn.unverified]', { operationId, resultKind: result.kind });
  await services.complete({ operationId, outcome: OrderFiscalPaymentReturnOutcome.Returned, verified: false });
  return 'returned';
}

function returnCommand(execution: PaymentReturnExecution, rrn: string | null) {
  const input = {
    amount: execution.amount,
    idCommand: execution.id_command,
    terminalDeviceId: execution.terminal_device_id,
  };

  return execution.command === OrderFiscalPaymentReturnCommand.Cancel
    ? mapCardCancelCommand({ ...input, universalId: rrnUniversalId(rrn ?? '') })
    : mapCardReturnCommand({ ...input, rrn: rrn ?? '' });
}

export function useCancelledPaymentReturn() {
  const adapter = getKkmServerAdapter();
  const options = { context: { suppressGlobalErrorToast: true } };
  const { mutate: start } = useMutation(StartOrderFiscalPaymentReturnDocument, options);
  const { mutate: complete } = useMutation(CompleteOrderFiscalPaymentReturnDocument, {
    ...options,
    refetchQueries: [...paymentQueryNames, 'GetOrderFiscalOperation'],
  });
  const services: CancelledPaymentReturnServices = {
    complete: async (input) =>
      requirePayload((await complete({ input }))?.data?.completeOrderFiscalPaymentReturn, 'Mors не записал возврат.'),
    start: async (operationId) =>
      requirePayload((await start({ operationId }))?.data?.startOrderFiscalPaymentReturn, 'Mors не начал возврат.'),
  };

  return {
    returnPayment: (
      operation: Pick<OrderFiscalOperationFieldsFragment, 'id' | 'return_command_id'>,
      rrn: string | null = null,
    ): Promise<CancelledPaymentReturnResult> =>
      apolloOperationGate.runExclusive('fiscalization', () =>
        returnCancelledPayment(adapter, services, operation, rrn),
      ),
  };
}
